import { CSSProperties, ReactNode } from 'react';
import { Drawer } from 'vaul';
import { Fn } from 'common/types.ts';
import { Col } from 'src/common/components/Flex';

type Props = {
  /** Whether the modal is open */
  isOpen?: boolean;
  /** Callback when the modal should close */
  onClose: Fn;
  /** Callback when open state changes */
  onOpen?: Fn;
  /** Content to render inside the modal */
  children: ReactNode;
  /** Title for accessibility (screen readers) */
  title?: string;
  /** Whether to show the overlay backdrop */
  showOverlay?: boolean;
  /** Custom overlay style */
  overlayStyle?: CSSProperties;
  /** Custom content container style */
  contentStyle?: CSSProperties;
  /** Custom class name for content container */
  contentClassName?: string;
  /** z-index for the overlay */
  overlayZIndex?: number;
  /** z-index for the content */
  contentZIndex?: number;
  /** Max width of the modal */
  maxWidth?: string;
  /** Border radius for corners */
  borderRadius?: string;
  /** Side margin on smaller screens */
  sideMargin?: string;
  /** Data test id for the content container */
  'data-testid'?: string;
  /** Whether to make the modal full screen */
  fullScreen?: boolean;
};

const Modal = ({
  isOpen,
  onClose,
  onOpen,
  children,
  title = 'modal',
  showOverlay = true,
  overlayStyle,
  contentStyle,
  contentClassName = 'bg-base',
  overlayZIndex,
  contentZIndex,
  maxWidth = '360px',
  borderRadius = '16px',
  'data-testid': testId,
  fullScreen = false,
}: Readonly<Props>) => {
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    } else {
      onOpen?.();
    }
  };

  return (
    <Drawer.Root open={isOpen} onOpenChange={handleOpenChange} modal={true} shouldScaleBackground={false}>
      <Drawer.Portal>
        {showOverlay && (
          <Drawer.Overlay
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: overlayZIndex,
              ...overlayStyle,
            }}
          />
        )}
        <Drawer.Content
          data-testid={testId}
          className={contentClassName}
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            margin: '0 auto',
            paddingBottom: fullScreen ? 0 : 24,
            height: fullScreen ? '100vh' : 'auto',
            background: 'transparent',
            maxWidth: fullScreen ? '100%' : maxWidth,
            overflow: 'hidden',
            zIndex: contentZIndex,
            direction: 'rtl',
            ...contentStyle,
          }}
        >
          {title && (
            <Drawer.Title style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>{title}</Drawer.Title>
          )}
          <Col className="fw fh" style={{ borderRadius }}>
            {children}
          </Col>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default Modal;
